import {type RevaLanguage, selectByLanguage} from "@reva/shared/i18n";

import {WebError} from "../api/web-error";
import {ErrorI18n} from "../i18n";
import type {ApiQueryError} from "../redux/types";

export const X_ERROR_KEY = "x-error";
export const X_ERROR_SEPARATOR = "/ ";

export interface XErrorData {
    "x-error": string;
    "x-error-de": string;
    "x-error-code": string;
    "x-error-code-description": string;
    "x-error-code-description-de": string;
    "x-info"?: string;
    "x-info-de"?: string;
}

export interface CustomErrorData {
    error: string;
    error_de: string;
    error_code: string;
    error_code_description: string;
    error_code_description_de: string;
    info?: string;
    info_de?: string;
}

export interface QueryError {
    error: string;
    errorArgs?: object;
    errorHasContactLink: boolean;
}

/**
 * Gets all x-error data from error headers.
 */
export function getXErrorData(error: WebError): XErrorData {
    const h = error.headers!;
    return {
        "x-error": h.get("x-error") ?? "",
        "x-error-de": h.get("x-error-de") ?? "",
        "x-error-code": h.get("x-error-code") ?? "",
        "x-error-code-description": h.get("x-error-code-description") ?? "",
        "x-error-code-description-de": h.get("x-error-code-description-de") ?? "",
        "x-info": h.get("x-info") ?? undefined,
        "x-info-de": h.get("x-info-de") ?? undefined,
    };
}

/**
 * Gets the localized error message from error data.
 */
export function getErrorDetail(errorData: XErrorData | CustomErrorData, language: RevaLanguage | undefined): string {
    if ("x-error" in errorData) {
        const detailDe = errorData["x-error-de"];
        const detailEn = errorData["x-error"];
        return (detailDe === "" ? detailEn : selectByLanguage(language, detailDe, detailEn)) ?? "";
    } else {
        return selectByLanguage(language, errorData.error_de, errorData.error) ?? "";
    }
}

/**
 * Localized backend error detail from a caught {@link ErrorI18n} (e.g. "Die Bewertung wurde bereits
 * abgeschlossen …"), or null when the error carries no structured backend message (network/500 errors) —
 * callers then fall back to a generic message.
 */
export function errorDetailFromErrorI18n(err: unknown, language: RevaLanguage | undefined): string | null {
    const errorData =
        err instanceof ErrorI18n ? (err.messageArgs as {errorData?: CustomErrorData} | undefined)?.errorData : null;
    if (!errorData) return null;
    return getErrorDetail(errorData, language) || null;
}

/**
 * Gets the localized error description from error data.
 */
export function getErrorDescription(
    errorData: XErrorData | CustomErrorData,
    language: RevaLanguage | undefined,
): string {
    if ("x-error" in errorData) {
        return (
            selectByLanguage(
                language,
                errorData["x-error-code-description-de"],
                errorData["x-error-code-description"],
            ) ?? ""
        );
    } else {
        return selectByLanguage(language, errorData.error_code_description_de, errorData.error_code_description) ?? "";
    }
}

/**
 * Formats caught error to query error.
 */
export function generateDefaultQueryError(error: ErrorI18n | Error): QueryError {
    if (error instanceof ErrorI18n) {
        return {
            error: error.message,
            errorArgs: {...error.messageArgs},
            errorHasContactLink: error.hasContactLink,
        };
    } else {
        return {
            error: "general.error.custom",
            errorArgs: {errorMessage: error.message},
            errorHasContactLink: true,
        };
    }
}

/**
 * Build API query error data for storage in a Redux Slice from a QueryError.
 */
export function buildApiQueryError(queryError: QueryError): ApiQueryError {
    return {
        error: queryError.error,
        errorArgs: queryError.errorArgs ?? null,
        hasContactLink: queryError.errorHasContactLink || false,
    };
}

/**
 * #19351: Localized message for a failed /user/profile, /user/password, /password-reset/* or
 * /email-verification/redeem request (raw {@link WebError}, not wrapped into an {@link ErrorI18n} — see
 * `UserProfileUpdateApiInterface`/`UserPasswordUpdateApiInterface`/`PasswordResetRequestApiInterface`/
 * `PasswordResetRedeemApiInterface`/`EmailVerificationRedeemApiInterface`).
 * A response with `structuredErrorStatus` (default 422; the password-reset request uses 429 for its rate
 * limit) carries reva-rest's QualifiedErrorMessageModel `{error, error_de, error_code}`, shown in the
 * current UI language. A 503 means account-management is currently unreachable. Anything else falls back
 * to the caller-provided generic text.
 *
 * `/email-verification/redeem` also answers 429 with no structured body (reva-rest's own IP throttle, not
 * account-management's) — since only one `structuredErrorStatus` can be selected here, its caller checks
 * that status itself before falling back to this function.
 */
export function resolveUserApiErrorMessage(
    err: unknown,
    language: RevaLanguage | undefined,
    unreachableMessage: string,
    genericMessage: string,
    structuredErrorStatus: number = 422,
): string {
    if (err instanceof WebError) {
        if (
            err.status === structuredErrorStatus &&
            typeof err.json?.error === "string" &&
            typeof err.json?.error_de === "string"
        ) {
            return selectByLanguage(language, err.json.error_de, err.json.error);
        }
        if (err.status === 503) {
            return unreachableMessage;
        }
    }
    return genericMessage;
}
